import type { VideoReview } from '@/api/endpoints'
import { getMarkdownPreviewText } from '@/components/Ui/MarkdownText'
import { HiOutlineClock, HiOutlinePlay, HiOutlineVideoCamera } from 'react-icons/hi2'
import { Link } from 'react-router-dom'

type VideoReviewCardProps = {
  review: VideoReview
}

function formatCreatedAt(value?: string | null): string | null {
  if (!value) {
    return null
  }

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

function formatDuration(value?: number | null): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return null
  }

  const totalSeconds = Math.floor(value)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }

  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

export default function VideoReviewCard({ review }: VideoReviewCardProps) {
  const duration = formatDuration(review.video?.duration_seconds)
  const createdAt = formatCreatedAt(review.createdAt)
  const previewImage = review.video?.preview_image || ''
  const hasLinkedProducts = review.productsCount > 0
  const description = getMarkdownPreviewText(review.description)
    || 'Откройте обзор, чтобы посмотреть видео и связанные товары.'

  return (
    <Link
      to={review.detailPath}
      className="group flex h-full flex-col overflow-hidden rounded-[28px] border border-[#dce8d9] bg-white transition-transform duration-200 hover:-translate-y-1"
    >
      <div className="relative overflow-hidden bg-[#eef5ee]">
        <div className="aspect-[16/10] overflow-hidden">
          {previewImage ? (
            <img
              src={previewImage}
              alt={review.title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_top,_#243b2f,_#0f172a_72%)] px-6 text-center text-sm font-semibold uppercase tracking-[0.24em] text-white/70">
              Видео обзор
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        <div className="absolute left-4 bottom-4 inline-flex items-center gap-2 rounded-full bg-white/92 px-4 py-2 text-sm font-semibold text-slate-950 shadow-lg">
          <HiOutlinePlay className="h-5 w-5" />
          {hasLinkedProducts ? 'Смотреть обзор' : 'Смотреть видео'}
        </div>

        {duration ? (
          <div className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-black/75 px-3 py-1.5 text-xs font-semibold text-white">
            <HiOutlineClock className="h-4 w-4" />
            {duration}
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="line-clamp-2 text-lg font-semibold leading-7 text-slate-950 sm:text-xl">
              {review.title}
            </p>
            {createdAt ? (
              <p className="mt-2 text-xs font-medium uppercase tracking-[0.18em] text-slate-400">
                Добавлено {createdAt}
              </p>
            ) : null}
            {hasLinkedProducts ? (
              <p className="mt-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                <HiOutlineVideoCamera className="h-4 w-4" />
                {review.productsCount} товаров в обзоре
              </p>
            ) : null}
          </div>
        </div>

        <p className="line-clamp-3 text-sm leading-6 text-slate-600">
          {description}
        </p>

        {hasLinkedProducts && review.productTitles.length > 0 ? (
          <div className="mt-auto border-t border-slate-100 pt-4">
            <p className="text-xs font-medium uppercase tracking-[0.24em] text-slate-400">
              Товары в видео
            </p>
            <p className="mt-2 line-clamp-2 text-sm leading-6 text-slate-500">
              {review.productTitles.join(', ')}
            </p>
          </div>
        ) : null}
      </div>
    </Link>
  )
}
